import { MapPin, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

/**
 * AdsPreview — demo estático con 4 tarjetas simulando ofertas cercanas.
 * Se muestra en la pantalla de bienvenida al escanear el QR para que el usuario
 * entienda visualmente cómo se verán las promociones cuando active su radar.
 * Nada de esto llama al backend: es 100% mock para onboarding.
 */
const DEMO_ADS = [
  {
    id: "demo-1",
    business: "Boutique Aria",
    distance_m: 22,
    style: { from: "#FF007A", to: "#FFB800" },
    badge: "SOLO HOY",
    emoji: "✨",
    headline: "20% ADICIONAL",
    subheadline: "A ti que estás aquí: 20% extra en compras +$1,000",
    accent_text: "#000",
  },
  {
    id: "demo-2",
    business: "Café Nube",
    distance_m: 38,
    style: { from: "#6D28D9", to: "#3B82F6" },
    badge: "REGALO",
    emoji: "☕",
    headline: "LATTE GRATIS",
    subheadline: "Con la compra de un croissant, hasta agotar existencias",
    accent_text: "#FFF",
  },
  {
    id: "demo-3",
    business: "Sushi Ginza",
    distance_m: 41,
    style: { from: "#0F172A", to: "#DC2626" },
    badge: "2X1",
    emoji: "🍣",
    headline: "ROLL A LA MITAD",
    subheadline: "Compra 1 roll premium, llévate el 2do gratis · 12-4pm",
    accent_text: "#FFF",
  },
  {
    id: "demo-4",
    business: "Óptica Miró",
    distance_m: 47,
    style: { from: "#059669", to: "#84CC16" },
    badge: "-40%",
    emoji: "👓",
    headline: "MICAS ANTIFATIGA",
    subheadline: "40% en el 2do par + examen visual gratis",
    accent_text: "#FFF",
  },
];

export default function AdsPreview() {
  return (
    <div className="relative" data-testid="ads-preview-demo">
      {/* Header con leyenda */}
      <div className="flex items-start gap-3 mb-4 px-1">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-[#FF007A] text-white grid place-items-center shadow-lg shadow-[#FF007A]/30">
          <Sparkles className="w-4 h-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-[#FF007A]">
            Así se verá tu radar
          </div>
          <h3 className="font-heading font-extrabold text-lg leading-tight text-neutral-900">
            Las promociones te aparecerán en pantalla
          </h3>
          <p className="text-sm text-neutral-600 mt-0.5 leading-snug">
            Selecciona las que te interesen. Ignora el resto — <strong>tú decides</strong>, sin registros ni spam.
          </p>
        </div>
      </div>

      {/* Simulated feed - horizontal scroll on mobile, grid on tablets */}
      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-2 px-2 no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        data-testid="ads-preview-scroll"
      >
        {DEMO_ADS.map((ad, idx) => (
          <DemoAdCard key={ad.id} ad={ad} index={idx} />
        ))}
      </div>

      {/* Meta strip */}
      <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-neutral-500">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF007A] animate-pulse" />
        <span>Ejemplo — 4 comercios en tu zona</span>
      </div>
    </div>
  );
}

function DemoAdCard({ ad, index }) {
  const { business, distance_m, style, badge, emoji, headline, subheadline, accent_text } = ad;
  const bg = `linear-gradient(135deg, ${style.from} 0%, ${style.to} 100%)`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.08, duration: 0.4 }}
      className="shrink-0 snap-start w-[240px] rounded-3xl p-4 shadow-xl shadow-black/10 relative overflow-hidden"
      style={{ background: bg, color: accent_text }}
      data-testid={`ads-preview-card-${ad.id}`}
    >
      {/* Grain / shine overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4), transparent 40%)" }}
      />
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.35), transparent 60%)" }}
      />

      {/* Header row */}
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[9px] uppercase font-black tracking-widest opacity-95">{business}</div>
          <div className="flex items-center gap-1 text-[10px] mt-0.5 opacity-90">
            <MapPin className="w-2.5 h-2.5" />
            <span>a {distance_m} m</span>
          </div>
        </div>
        <span
          className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
          style={{ background: accent_text === "#000" ? "#000" : "#FFF", color: accent_text === "#000" ? "#FFF" : style.from }}
        >
          {badge}
        </span>
      </div>

      {/* Emoji */}
      <div className="relative text-3xl mt-2 drop-shadow">{emoji}</div>

      {/* Headline */}
      <div className="relative font-heading font-black text-[18px] leading-tight tracking-tight drop-shadow-lg mt-0.5">
        {headline}
      </div>

      {/* Subheadline */}
      <div className="relative text-[11px] leading-snug mt-1.5 opacity-95">
        {subheadline}
      </div>

      {/* CTAs */}
      <div className="relative mt-3 grid grid-cols-[1fr_auto] gap-1.5">
        <div className="rounded-full bg-black/90 text-white grid place-items-center font-bold text-[10px] h-8 tracking-wide">
          SABER MÁS →
        </div>
        <div className="rounded-full bg-white/25 grid place-items-center font-bold text-[10px] h-8 px-3">
          Borrar
        </div>
      </div>
    </motion.div>
  );
}
